import { Mic, Square, X, Headphones, FileText, Quote } from "lucide-react";
import { ModeSwitch } from "@/components/session/ModeSwitch";
import { st, CONVO_HINTS, fmt } from "@/lib/sessionStates";

const MicButton = ({ state, convo, onMic, busy, size }) => {
  const s = st(state);
  const listening = state === "listening";
  const thinking = state === "reflecting";
  return (
    <button
      data-testid="mic-btn"
      data-mic-state={state}
      onClick={onMic}
      disabled={busy}
      aria-busy={thinking}
      aria-label={listening ? "Finish speaking" : state === "speaking" ? "Stop Griot and take the floor" : "Speak to Griot"}
      className={`relative flex shrink-0 items-center justify-center rounded-full transition-[transform,box-shadow] duration-500 active:scale-[0.96] ${thinking ? "cursor-default" : "disabled:opacity-55"}`}
      style={{
        width: size,
        height: size,
        transitionTimingFunction: "cubic-bezier(0.16, 1, 0.3, 1)",
        background: listening
          ? "var(--text-main)"
          : "radial-gradient(circle at 34% 28%, color-mix(in srgb, var(--lab-accent) 86%, #F4EFE6) 0%, var(--lab-accent) 60%, color-mix(in srgb, var(--lab-accent) 70%, #0C0C0B) 100%)",
        color: "#0C0C0B",
        boxShadow: `0 8px 22px rgba(0,0,0,0.45), 0 0 ${state === "idle" ? 20 : state === "speaking" ? 56 : 44}px color-mix(in srgb, ${s.color} ${state === "idle" ? 28 : 40}%, transparent)`,
      }}
    >
      {thinking && (
        <>
          <span
            data-testid="mic-thinking-arc"
            className="spin-slow absolute inset-[-7px] rounded-full border-2"
            style={{ borderColor: "transparent", borderTopColor: s.color, borderRightColor: `${s.color}55` }}
          />
          <span className="soft-pulse absolute inset-[-7px] rounded-full" style={{ boxShadow: `0 0 26px ${s.color}` }} />
        </>
      )}
      {!thinking && (listening || (convo && state !== "reflecting")) && (
        <span className="breathe absolute inset-[-5px] rounded-full border" style={{ borderColor: s.color }} />
      )}
      {listening ? <Square size={size * 0.3} fill="currentColor" /> : <Mic size={size * 0.42} />}
    </button>
  );
};

const StopButton = ({ onClick, size }) => (
  <button
    data-testid="stop-speaking-btn"
    onClick={onClick}
    aria-label="Stop Griot speaking"
    title="Stop"
    className="ember-in relative flex shrink-0 items-center justify-center rounded-full border transition-transform duration-300 active:scale-[0.96]"
    style={{
      width: size,
      height: size,
      borderColor: "#E66A4E",
      color: "#E66A4E",
      background: "rgba(230,106,78,0.12)",
      boxShadow: "0 0 24px rgba(230,106,78,0.28)",
    }}
  >
    <Square size={size * 0.3} fill="currentColor" />
  </button>
);

const Chip = ({ icon: Icon, label, active, onClick, testId, title, iconOnly }) => (
  <button
    data-testid={testId}
    onClick={onClick}
    aria-pressed={!!active}
    aria-label={title}
    title={title}
    className={`inline-flex shrink-0 items-center gap-1.5 rounded-full border font-mono text-[9.5px] uppercase tracking-[0.14em] transition-colors duration-300 ${iconOnly ? "h-9 w-9 justify-center" : "px-3 py-[7px]"}`}
    style={active
      ? { borderColor: "color-mix(in srgb, var(--lab-accent) 55%, transparent)", color: "var(--lab-accent)", background: "color-mix(in srgb, var(--lab-accent) 14%, transparent)" }
      : { borderColor: "var(--border-linen)", color: "var(--text-faint)", background: "rgba(12,12,11,0.4)" }}
  >
    <Icon size={13} /> {!iconOnly && label}
  </button>
);

const Readout = ({ state, convo, elapsed, cancelRecording }) => {
  const s = st(state);
  const hint = convo ? CONVO_HINTS[state] || s.hint : s.hint;
  return (
    <div className="min-w-0">
      <div className="flex items-center gap-3">
        <p data-testid="waveform-state-label" className="truncate font-mono text-[10px] uppercase tracking-[0.24em]" style={{ color: s.color }}>
          {s.label}
        </p>
        {state === "listening" && (
          <>
            <span data-testid="rec-timer" className="font-mono text-[10px]" style={{ color: "var(--text-dim)" }}>{fmt(elapsed)}</span>
            <button
              data-testid="cancel-rec-btn"
              onClick={cancelRecording}
              aria-label="Let this one go"
              className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full border transition-colors hover:text-[#F4EFE6]"
              style={{ borderColor: "var(--border-linen)", color: "var(--text-faint)" }}
            >
              <X size={10} />
            </button>
          </>
        )}
      </div>
      <p className="mt-1.5 truncate text-[12px]" style={{ color: "var(--text-faint)" }}>{hint}</p>
    </div>
  );
};

// One instrument: voice, readout, mode and how you'd rather receive the answer.
export const Console = ({ state, convo, toggleConvo, onMic, cancelRecording, elapsed, busy, compact, trailing, read, toggleRead, passages, togglePassages, sourcesAvailable, stopSpeaking }) => {
  const speaking = state === "speaking";
  const chips = (iconOnly) => (
    <>
      <Chip
        iconOnly={iconOnly}
        testId="read-mode-toggle"
        icon={read ? FileText : Headphones}
        label={read ? "Read" : "Listen"}
        active={read}
        onClick={toggleRead}
        title={read ? "Reading — answers are written and Griot stays quiet" : "Listening — Griot speaks the answers"}
      />
      {sourcesAvailable && (
        <Chip
          iconOnly={iconOnly}
          testId="passages-toggle"
          icon={Quote}
          label="Passages"
          active={passages}
          onClick={togglePassages}
          title={passages ? "Hide the passages Griot leaned on" : "Show the passages Griot leaned on"}
        />
      )}
    </>
  );

  if (compact) {
    return (
      <div className="console-skin w-full rounded-[26px] p-4" data-testid="acoustic-console">
        <div className="flex items-center gap-4">
          {speaking
            ? <StopButton onClick={stopSpeaking} size={58} />
            : <MicButton state={state} convo={convo} onMic={onMic} busy={busy} size={58} />}
          <div className="min-w-0 flex-1">
            <Readout state={state} convo={convo} elapsed={elapsed} cancelRecording={cancelRecording} />
          </div>
          {trailing}
        </div>
        <div className="mt-4 flex items-center gap-2">
          <div className="min-w-0 flex-1"><ModeSwitch convo={convo} toggleConvo={toggleConvo} full compact /></div>
          {chips(true)}
        </div>
      </div>
    );
  }

  return (
    <div className="console-skin flex w-[768px] items-center gap-4 rounded-[28px] p-5" data-testid="acoustic-console">
      <MicButton state={state} convo={convo} onMic={onMic} busy={busy} size={64} />
      {speaking && <StopButton onClick={stopSpeaking} size={44} />}
      <span className="h-11 w-px shrink-0" style={{ background: "var(--border-linen)" }} />
      <div className="min-w-0 flex-1">
        <Readout state={state} convo={convo} elapsed={elapsed} cancelRecording={cancelRecording} />
      </div>
      <ModeSwitch convo={convo} toggleConvo={toggleConvo} />
      <div className="flex shrink-0 flex-col gap-1.5">{chips(false)}</div>
    </div>
  );
};
